import html2pdf from "html2pdf.js";

const ROOT_ID = "pwr-report-root";
const PDF_MODE_CLASS = "pwr-pdf-mode";
const STRIP_SELECTOR = '[data-pdf-strip="appendix"]';

function buildFilename(report) {
  const rawDomain = report?.domain || "report";
  const slug = rawDomain
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "report";
  const dateSrc = report?.completed_at ? new Date(report.completed_at) : new Date();
  const yyyy = dateSrc.getFullYear();
  const mm = String(dateSrc.getMonth() + 1).padStart(2, "0");
  const dd = String(dateSrc.getDate()).padStart(2, "0");
  return `lt-website-report-${slug}-${yyyy}-${mm}-${dd}.pdf`;
}

async function waitForImages(rootEl) {
  const imgs = Array.from(rootEl.querySelectorAll("img"));
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      if (typeof img.decode === "function") {
        return img.decode().catch(() => undefined);
      }
      return new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    })
  );
}

export async function generatePdf(report) {
  const liveRoot = document.getElementById(ROOT_ID);
  if (!liveRoot) {
    throw new Error("Report content not found");
  }

  // Match the live width so layout doesn't reflow when cloned offscreen.
  const liveWidth = liveRoot.getBoundingClientRect().width;

  const stage = document.createElement("div");
  stage.style.cssText = [
    "position: fixed",
    "left: -99999px",
    "top: 0",
    `width: ${liveWidth}px`,
    "z-index: -1",
    "pointer-events: none",
  ].join(";");

  const clone = liveRoot.cloneNode(true);
  clone.classList.add(PDF_MODE_CLASS);

  // Remove the Technical Appendix subtree entirely.
  clone.querySelectorAll(STRIP_SELECTOR).forEach((el) => el.remove());

  stage.appendChild(clone);
  document.body.appendChild(stage);

  try {
    await waitForImages(clone);

    const filename = buildFilename(report);

    const opts = {
      filename,
      margin: 0.5,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: {
        useCORS: true,
        backgroundColor: null,
        scale: 2,
        logging: false,
      },
      jsPDF: {
        unit: "in",
        format: "letter",
        orientation: "portrait",
      },
      pagebreak: { mode: ["css", "legacy"] },
    };

    await html2pdf().set(opts).from(clone).save();
  } finally {
    if (stage.parentNode) stage.parentNode.removeChild(stage);
  }
}
